import qrcode from 'qrcode-generator';

export type QrErrorCorrectionLevel = 'L' | 'M' | 'Q' | 'H';
type QrMode = 'Numeric' | 'Alphanumeric' | 'Byte';

export const QR_MIN_MODULE_MM = 0.33;
export const QR_MIN_SIZE_INCHES = 0.15;
export const QR_QUIET_ZONE_MODULES = 4;
export const QR_QUIET_ZONE_THRESHOLD_INCHES = 0.75;
export const QR_DEFAULT_ERROR_CORRECTION: QrErrorCorrectionLevel = 'Q';
export const QR_DESIGN_PAYLOAD = 'HTTPS://1A4.COM/5LN8CBN1CRSO1ZBGHON4';

// Keep this mode selection in sync with packages/qr-code-styling/src/tools/getMode.ts.
// Sizing and rendering must encode the payload in the same mode to agree on module count.
export function getQrMode(value: string): QrMode {
  if (/^[0-9]*$/.test(value)) return 'Numeric';
  if (/^[0-9A-Z $%*+\-./:]*$/.test(value)) return 'Alphanumeric';
  return 'Byte';
}

export type QrGeometryOptions = {
  value: string;
  requestedSize: number;
  dpi: number;
  outputFormat?: 'pdf' | 'zpl';
  margin?: number;
  errorCorrectionLevel?: QrErrorCorrectionLevel;
  snapToModuleGrid?: boolean;
  includeQuietZone?: boolean;
};

export type QrGeometry = {
  moduleCount: number;
  moduleSize: number;
  quietZoneModules: number;
  margin: number;
  minimumSize: number;
  size: number;
  wasAdjusted: boolean;
};

// Scale by the actual module width, which can be much smaller than size/count
// when explicit margins consume most of a PDF QR's canvas. Keep the existing
// 4x raster for larger symbols and at least four source pixels per module.
export function getQrRasterWidth(geometry: QrGeometry): number {
  return Math.ceil(geometry.size * Math.max(4, 4 / geometry.moduleSize) - 1e-9);
}

export type QrCacheKeyOptions = {
  value: string;
  width: number;
  height: number;
  margin: number;
  fg?: string;
  bg?: string;
  errorCorrectionLevel?: QrErrorCorrectionLevel;
  dpi?: number;
  namespace?: string;
};

export function getQrCacheKey({
  value,
  width,
  height,
  margin,
  fg,
  bg,
  errorCorrectionLevel,
  dpi = 72,
  namespace,
}: QrCacheKeyOptions): string {
  const parts = [
    value,
    Math.round(width),
    Math.round(height),
    margin,
    fg,
    bg,
    errorCorrectionLevel ?? QR_DEFAULT_ERROR_CORRECTION,
    `dpi:${dpi}`,
  ];
  return namespace ? [namespace, ...parts].join('|') : parts.join('|');
}

export function getQrModuleCount(
  value: string,
  errorCorrectionLevel: QrErrorCorrectionLevel = QR_DEFAULT_ERROR_CORRECTION,
): number {
  const qr = qrcode(0, errorCorrectionLevel);
  const data = value || ' ';
  qr.addData(data, getQrMode(data));
  qr.make();
  return qr.getModuleCount();
}

export function getMinimumQrModuleSize(dpi: number): number {
  const safeDpi = Number.isFinite(dpi) && dpi > 0 ? dpi : 72;
  // ZPL units are physical printer dots: every encoded module needs at least
  // one dot. PDF units are points and can contain higher-resolution images.
  return Math.max(1, Math.ceil((QR_MIN_MODULE_MM * safeDpi) / 25.4));
}

function getMinimumSize(
  moduleCount: number,
  moduleSize: number,
  requestedMargin: number,
  quietZoneModules: number,
): number {
  const quietZone = quietZoneModules * moduleSize;
  return moduleCount * moduleSize + 2 * Math.max(requestedMargin, quietZone);
}

export function getQrGeometry({
  value,
  requestedSize,
  dpi,
  outputFormat = 'pdf',
  margin = 0,
  errorCorrectionLevel = QR_DEFAULT_ERROR_CORRECTION,
  snapToModuleGrid = false,
  includeQuietZone = false,
}: QrGeometryOptions): QrGeometry {
  const moduleCount = getQrModuleCount(value, errorCorrectionLevel);
  const minimumModuleSize = getMinimumQrModuleSize(dpi);
  const requestedMargin = Number.isFinite(margin)
    ? Math.max(0, outputFormat === 'zpl' ? Math.ceil(margin) : margin)
    : 0;
  const safeDpi = Number.isFinite(dpi) && dpi > 0 ? dpi : 72;
  const getQuietZoneModules = (size: number): number => includeQuietZone
    ? QR_QUIET_ZONE_MODULES
    : size / safeDpi < QR_QUIET_ZONE_THRESHOLD_INCHES ? 1 : 2;
  let minimumSize = Math.max(Math.ceil(QR_MIN_SIZE_INCHES * safeDpi), Math.ceil(2 * requestedMargin + 1));
  if (outputFormat === 'zpl' || includeQuietZone) {
    const minimumRenderableModuleSize = includeQuietZone ? minimumModuleSize : 1;
    minimumSize = Math.max(minimumSize, getMinimumSize(moduleCount, minimumRenderableModuleSize, requestedMargin, getQuietZoneModules(minimumSize)));
    minimumSize = Math.max(minimumSize, getMinimumSize(moduleCount, minimumRenderableModuleSize, requestedMargin, getQuietZoneModules(minimumSize)));
  }
  const roundedSize = Number.isFinite(requestedSize) ? Math.round(requestedSize) : minimumSize;
  let size = Math.max(minimumSize, roundedSize);
  let quietZoneModules = getQuietZoneModules(size);
  const legacyMinimumSize = getMinimumSize(moduleCount, minimumModuleSize, requestedMargin, quietZoneModules);
  const availableModuleSize = Math.min(
    (size - 2 * requestedMargin) / moduleCount,
    size / (moduleCount + 2 * quietZoneModules),
  );
  let moduleSize: number;

  if (outputFormat !== 'zpl' && !snapToModuleGrid && !includeQuietZone) {
    moduleSize = availableModuleSize;
  } else if (size < legacyMinimumSize && !includeQuietZone) {
    moduleSize = outputFormat === 'zpl' ? Math.floor(availableModuleSize) : availableModuleSize;
  } else if (snapToModuleGrid) {
    moduleSize = Math.max(minimumModuleSize, Math.round(availableModuleSize));
    size = getMinimumSize(moduleCount, moduleSize, requestedMargin, quietZoneModules);
    quietZoneModules = getQuietZoneModules(size);
    size = getMinimumSize(moduleCount, moduleSize, requestedMargin, quietZoneModules);
  } else {
    moduleSize = Math.floor(availableModuleSize);
  }

  return {
    moduleCount,
    moduleSize,
    quietZoneModules,
    margin: outputFormat !== 'zpl' && !includeQuietZone && (!snapToModuleGrid || size < legacyMinimumSize)
      ? (size - moduleCount * moduleSize) / 2
      : Math.floor((size - moduleCount * moduleSize) / 2),
    minimumSize,
    size,
    wasAdjusted: size !== roundedSize,
  };
}

// Maximum 12-byte package identity plus the largest supported Int32 index,
// encoded by the Retail ID Base36 codec on the standard production domain.
export const QR_MAX_STANDARD_RUNTIME_PAYLOAD = 'HTTPS://1A4.COM/2ZRW1727U0MGG6UGS60VXE42DXJ';

export function getQrTemplateGeometry(options: QrGeometryOptions): QrGeometry {
  if (options.outputFormat !== 'zpl') return getQrGeometry(options);
  const level = options.errorCorrectionLevel ?? QR_DEFAULT_ERROR_CORRECTION;
  const runtimeModules = getQrModuleCount(QR_MAX_STANDARD_RUNTIME_PAYLOAD, level);
  const designModules = getQrModuleCount(options.value, level);
  return getQrGeometry({
    ...options,
    value: runtimeModules > designModules ? QR_MAX_STANDARD_RUNTIME_PAYLOAD : options.value,
  });
}

export function getQrDesignGeometry(
  requestedSize: number,
  dpi: number,
  margin = 0,
  errorCorrectionLevel: QrErrorCorrectionLevel = QR_DEFAULT_ERROR_CORRECTION,
): QrGeometry {
  return getQrGeometry({
    value: QR_DESIGN_PAYLOAD,
    requestedSize,
    dpi,
    margin,
    errorCorrectionLevel,
  });
}

export function getQrMargin(options: QrGeometryOptions): { minimum: number; maximum: number; value: number } {
  const geometry = getQrTemplateGeometry({ ...options, margin: 0 });
  const minimum = geometry.margin;
  const availableMargin = (geometry.size - (options.outputFormat === 'zpl' ? geometry.moduleCount : 1)) / 2;
  const maximum = options.outputFormat === 'zpl' ? Math.floor(availableMargin) : availableMargin;
  const requestedMargin = Number.isFinite(options.margin) ? options.margin! : minimum;
  return {
    minimum,
    maximum,
    value: Math.min(maximum, Math.max(minimum, options.outputFormat === 'zpl' ? Math.ceil(requestedMargin) : requestedMargin)),
  };
}

export function getResizedQrMargin(options: QrGeometryOptions, previousSize: number): number {
  const previousMinimum = getQrMargin({ ...options, requestedSize: previousSize, margin: 0 }).minimum;
  return getQrMargin({
    ...options,
    margin: (options.margin ?? 0) <= previousMinimum + 1e-9 ? 0 : options.margin,
  }).value;
}

export function getResizedQrDesignGeometry({
  value = QR_DESIGN_PAYLOAD,
  startLeft,
  startTop,
  startWidth,
  startHeight,
  requestedWidth,
  requestedHeight,
  resizeWest,
  resizeNorth,
  dpi,
  margin = 0,
  errorCorrectionLevel = QR_DEFAULT_ERROR_CORRECTION,
  outputFormat = 'pdf',
}: {
  value?: string;
  startLeft: number;
  startTop: number;
  startWidth: number;
  startHeight: number;
  requestedWidth: number;
  requestedHeight: number;
  resizeWest: boolean;
  resizeNorth: boolean;
  dpi: number;
  outputFormat?: 'pdf' | 'zpl';
  margin?: number;
  errorCorrectionLevel?: QrErrorCorrectionLevel;
}): { x: number; y: number; size: number; margin: number } {
  const size = getQrTemplateGeometry({
    value,
    requestedSize: Math.min(requestedWidth, requestedHeight),
    dpi,
    outputFormat,
    margin: 0,
    errorCorrectionLevel,
  }).size;
  return {
    x: resizeWest ? startLeft + startWidth - size : startLeft,
    y: resizeNorth ? startTop + startHeight - size : startTop,
    size,
    margin: getResizedQrMargin({ value, requestedSize: size, dpi, outputFormat, margin, errorCorrectionLevel }, Math.min(startWidth, startHeight)),
  };
}

export function assertQrSizeIsSafe(options: QrGeometryOptions): QrGeometry {
  const geometry = getQrGeometry(options);
  if (!Number.isFinite(options.requestedSize) || options.requestedSize < geometry.minimumSize || geometry.wasAdjusted || geometry.size !== options.requestedSize) {
    throw new Error(
      `QR size ${Math.round(options.requestedSize)} is unsafe for this payload at ${options.dpi} DPI; minimum size is ${geometry.minimumSize}.`,
    );
  }
  return geometry;
}
